/**
 * The client Sprint calendar's view model (LG-412): everything the rail
 * derives from a `SprintCalendar` payload that is not itself a server rule.
 *
 * The server owns the reach (`isCovered`, `creditsShort`) and the legality of
 * a move (`canSkip` / `canUnskip`). Nothing here re-derives either. What lives
 * here is presentation: where a week sits against the clock, which rows are
 * behind the client, where the reach line goes, and what each state is called.
 */
import type { RouterOutputs } from '@/lib/trpc'

export type SprintCalendar = RouterOutputs['dashboard']['sprints']['calendar']
export type SprintCalendarWeek = SprintCalendar['weeks'][number]

/**
 * Where a week sits against the clock. Lock-in is the Saturday before the
 * Monday start, so a locked week is two different rows on screen -- one
 * still ahead (`locking`), one underway (`running`). The server names every
 * boundary itself (`after_lock_in` is the weekend before the start,
 * `after_start` the week underway, LG-1004), and nothing here consults the
 * browser's clock: `canSkip` was decided on the server's clock, and judging
 * a row on the browser's would let a fast client clock say "Locked" above a
 * skip button the server still allows.
 */
export type WeekPhase = 'upcoming' | 'locking' | 'running' | 'ended'

export function weekPhase(week: SprintCalendarWeek): WeekPhase {
  switch (week.phase) {
    case 'after_end':
      return 'ended'
    case 'before_lock_in':
      return 'upcoming'
    case 'after_lock_in':
      return 'locking'
    case 'after_start':
      return 'running'
  }
}

/**
 * Whether the client is (or will be) in the week. `isCovered` already folds in
 * both halves -- a credit spent (`locked`, free weeks included) and a credit
 * the balance is projected to spend -- so a `locked` week that has since ended
 * reads as joined without any date logic here.
 */
export function isJoined(week: SprintCalendarWeek): boolean {
  return week.isCovered
}

/** A week that ran with the client in it -- the only past rows worth a look back. */
export function ranWithClient(
  week: SprintCalendarWeek,
  phase: WeekPhase,
): boolean {
  return phase === 'ended' && week.status === 'locked'
}

/**
 * The week underway that locked before the client's participation could. A
 * week they chose to skip is not a missed lock-in, whatever the clock says.
 */
export function missedLockIn(
  week: SprintCalendarWeek,
  phase: WeekPhase,
): boolean {
  return (
    phase === 'running' && week.status !== 'locked' && week.status !== 'skipped'
  )
}

/**
 * The week the reach line sits under: the last week in date order the client
 * is in that has not already ended. The payload has no `lastFundedId`; it is
 * a reading of `isCovered`, which is why it is derived here and nowhere else.
 */
export function lastCoveredWeekId(
  weeks: readonly SprintCalendarWeek[],
): string | null {
  let last: string | null = null
  for (const week of weeks) {
    if (week.isCovered && weekPhase(week) !== 'ended') last = week.sprintId
  }
  return last
}

/**
 * Active is everything not yet over, in date order; Historical is what has
 * ended, newest first, because history is read backwards from where you are.
 */
export function splitWeeks(weeks: readonly SprintCalendarWeek[]): {
  active: SprintCalendarWeek[]
  historical: SprintCalendarWeek[]
} {
  const active: SprintCalendarWeek[] = []
  const historical: SprintCalendarWeek[] = []
  for (const week of weeks) {
    if (weekPhase(week) === 'ended') historical.push(week)
    else active.push(week)
  }
  historical.reverse()
  return { active, historical }
}

/**
 * The Sprints the client holds, as the rondal shows them: the week they are in
 * now (only if they locked into it), every week ahead their Sprints reach, and
 * the ones they skipped. Suspended and finished weeks are not held Sprints.
 */
export function rondalWeeks(
  weeks: readonly SprintCalendarWeek[],
): SprintCalendarWeek[] {
  return weeks.filter((week) => {
    if (week.sprintStatus === 'suspended') return false
    const phase = weekPhase(week)
    if (phase === 'ended') return false
    if (phase === 'running') return week.status === 'locked'
    return week.isCovered || week.status === 'skipped'
  })
}

/** Only the states a client would be surprised by earn a word. */
export function weekNote(
  week: SprintCalendarWeek,
  phase: WeekPhase,
): string | null {
  if (week.sprintStatus === 'suspended') return 'Program paused'
  if (week.status === 'skipped') return 'Skipped'
  if (missedLockIn(week, phase)) return 'Underway without you'
  if (week.isFree && week.isCovered) return 'Free'
  return null
}

/** What the participation glyph says out loud. */
export function participationLabel(
  week: SprintCalendarWeek,
  phase: WeekPhase,
): string {
  if (missedLockIn(week, phase)) return 'This Sprint locked before you joined'
  if (isJoined(week)) {
    return week.isFree
      ? 'You are in this Sprint, free of charge'
      : 'You are in this Sprint'
  }
  if (week.status === 'skipped') return 'You skipped this week'
  if (week.sprintStatus === 'suspended') return 'No Sprint runs this week'
  if (phase === 'ended') return 'You were not in this Sprint'
  const short = week.creditsShort ?? 0
  if (short === 1) return 'You are not in this Sprint — one Sprint short'
  if (short > 1) return `You are not in this Sprint — ${short} Sprints short`
  return 'You are not in this Sprint'
}

/** "8 Sprints left" / "1 Sprint left" -- the only place the balance is worded. */
export function sprintsLeftLabel(creditBalance: number): string {
  return creditBalance === 1 ? '1 Sprint left' : `${creditBalance} Sprints left`
}

/** Two or fewer, but not none: the tile and the reach line change tone. */
export function isRunningLow(creditBalance: number): boolean {
  return creditBalance > 0 && creditBalance <= 2
}

const WEEK_RANGE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

/** "Sep 14 – Sep 20" -- the week, not two instants the reader has to subtract. */
export function formatWeek(week: SprintCalendarWeek): string {
  const lastDay = new Date(week.endsAt)
  lastDay.setUTCDate(lastDay.getUTCDate() - 1)
  return `${WEEK_RANGE.format(new Date(week.startsAt))} – ${WEEK_RANGE.format(lastDay)}`
}

const LOCK_DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

/** "Sat, Sep 19" -- the moment the week stops being changeable. */
export function formatLockDay(week: SprintCalendarWeek): string {
  return LOCK_DAY.format(new Date(week.locksAt))
}

/** "Sep 19" -- the same moment where a phone has no room for the weekday. */
export function formatLockDayShort(week: SprintCalendarWeek): string {
  return WEEK_RANGE.format(new Date(week.locksAt))
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

export function monthKey(week: SprintCalendarWeek): string {
  const starts = new Date(week.startsAt)
  return `${starts.getUTCFullYear()}-${starts.getUTCMonth()}`
}

export function formatMonth(week: SprintCalendarWeek): string {
  return MONTH_LABEL.format(new Date(week.startsAt))
}
